<template>
    <tech-template>

        <!-- 📅 Sélecteur de semaine -->
        <div class="bg-white shadow-sm sticky top-0 z-10">
            <div class="flex items-center justify-between px-3 py-2 border-b">
                <button @click="prevWeek" class="p-2 active:scale-95">
                    <i class="fas fa-chevron-left text-gray-600"></i>
                </button>

                <p class="text-sm font-medium text-gray-700">
                    {{ weekLabel }}
                </p>

                <button @click="nextWeek" class="p-2 active:scale-95">
                    <i class="fas fa-chevron-right text-gray-600"></i>
                </button>
            </div>

            <div class="flex overflow-x-auto no-scrollbar px-2 py-2 gap-2">
                <button
                    v-for="day in weekDays"
                    :key="day.date"
                    @click="selectDate(day.date)"
                    class="flex flex-col items-center justify-center min-w-[56px] px-2 py-2 rounded-xl text-sm transition-all"
                    :class="[day.date === selectedDate
                        ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow'
                        : 'bg-gray-100 text-gray-700',
                        day.isToday ? '' : 'px-5'
                    ]"
                >
                    <span class="text-xs uppercase">
                        {{ day.label }}
                    </span>
                    <span class="text-lg font-semibold">
                        {{ day.day }}
                    </span>
                    <span v-if="day.isToday" class="text-[10px] mt-0.5">
                        Aujourd’hui
                    </span>
                </button>
            </div>
        </div>

        <!-- Liste d’interventions -->
        <template v-if="loadingGetData">
            <div class="flex items-center justify-center h-64">
                <i class="fas fa-spinner fa-spin text-4xl text-gray-500"></i>
            </div>
        </template>

        <template v-if="!loadingGetData">
            <div class="p-4 space-y-3">
                <template v-if="!loadingGetData">

                    <div v-if="interventions.length === 0" class="p-6 text-center text-gray-400">
                        Aucune intervention prévue ce jour-là.
                    </div>

                    <div class="p-4 space-y-4">

                        <div
                            v-for="item in interventions"
                            :key="item.id"
                            class="bg-white rounded-2xl shadow-sm border border-gray-100 p-4"
                        >

                            <!-- ⏱ Heure + statut -->
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <span class="text-sm font-semibold text-gray-900">
                                        {{ item.start }} – {{ item.end }}
                                    </span>

                                    <span class="text-xs text-gray-400">
                                        ({{ formatDuration(item.duration) }})
                                    </span>
                                </div>

                                <span
                                    class="text-xs font-medium px-2 py-0.5 rounded-full"
                                    :class="statusClass(item.statutCode)"
                                >
                                    {{ statusLabel(item.statutCode) }}
                                </span>
                            </div>

                            <!-- 👤 Client -->
                            <div class="mt-3">
                                <p class="text-sm font-semibold text-gray-800">
                                    {{ item.client }}
                                </p>
                                <p class="text-xs text-gray-500 leading-snug">
                                    {{ item.address }}
                                </p>
                            </div>

                            <!-- 🏷 Type -->
                            <div class="mt-3">
                                <span class="inline-block px-3 py-1 text-xs font-medium bg-red-50 text-red-700 rounded-full">
                                    {{ item.type }}
                                </span>
                            </div>

                            <!-- ▶ Action -->
                            <div class="mt-4">
                                <button
                                    @click="$router.push({ name: 'TechInterventionView', params: { id: item.id } })"
                                    class="w-full py-3 rounded-xl font-medium text-sm transition-all active:scale-95"
                                    :class="item.statutCode === 'en_cours'
                                        ? 'bg-orange-600 text-white animate-pulse'
                                        : 'bg-slate-900 text-white'"
                                >
                                    {{ item.statutCode === 'en_cours'
                                        ? 'Intervention en cours'
                                        : 'Accéder à l’intervention' }}
                                </button>
                            </div>

                        </div>

                    </div>
                </template>

            </div>
        </template>

    </tech-template>
</template>

<script>
import TechTemplate from '@/components/TechTemplate.vue'

export default {
    name: 'TechDayList',
    components: { TechTemplate },
    data() {
        const today = new Date()
        return {
            interventions: [],
            loadingGetData: false,
            selectedDate: new Date().toISOString().slice(0, 10),
            weekStart: this.getMonday(today)
        }
    },
    computed: {
        weekDays() {
            const days = []
            for (let i = 0; i < 7; i++) {
                const d = new Date(this.weekStart)
                d.setDate(d.getDate() + i)

                const iso = this.formatDateLocal(d)
                const todayIso = new Date().toISOString().slice(0, 10)

                days.push({
                    date: iso,
                    day: d.getDate(),
                    label: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
                    isToday: iso === todayIso
                })
            }
            return days
        },
        weekLabel() {
            const start = new Date(this.weekStart)
            const end = new Date(this.weekStart)
            end.setDate(end.getDate() + 6)

            return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} → ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`
        }
    },
    methods: {
        getMonday(date) {
            const d = new Date(date)
            d.setHours(12, 0, 0, 0) // 🔥 évite tout edge case DST / timezone
            const day = d.getDay() || 7
            d.setDate(d.getDate() - day + 1)
            return d
        },
        selectDate(date) {
            this.selectedDate = date
            this.getInterventions()
        },
        prevWeek() {
            this.weekStart.setDate(this.weekStart.getDate() - 7)
            this.weekStart = new Date(this.weekStart)
        },
        nextWeek() {
            this.weekStart.setDate(this.weekStart.getDate() + 7)
            this.weekStart = new Date(this.weekStart)
        },
        async getInterventions() {
            this.loadingGetData = true

            const response = await this.$request.post('/interventions/today', {
                technicien_id: this.$account.account.id,
                date: this.selectedDate
            })

            this.loadingGetData = false

            // if (response.not_ended == 1) {
            //     this.$router.push({ name: 'TechInterventionView', params: { id: response.intervention.id } })
            //     return
            // }

            this.interventions = response.map(item => ({
                id: item.id,
                start: item.heure_debut.substring(0, 5).replace(':', 'h'),
                end: item.heure_fin.substring(0, 5).replace(':', 'h'),
                duration: item.duree_estimee,
                distance: 0,
                client: item.client.nom + ' ' + item.client.prenom,
                address: item.adresse.adresse1 + ', ' + item.adresse.code_postal + ' ' + item.adresse.ville,
                type: item.intervention_type.nom,
                statutCode: item.statut.code
            }))
        },
        formatDateLocal(date) {
            const y = date.getFullYear()
            const m = String(date.getMonth() + 1).padStart(2, '0')
            const d = String(date.getDate()).padStart(2, '0')
            return `${y}-${m}-${d}`
        },

        formatDuration(minutes) {
            const h = Math.floor(minutes / 60)
            const m = minutes % 60
            return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`
        },
        statusLabel(code) {
            switch (code) {
                case 'en_cours': return 'En cours'
                case 'terminee': return 'Terminée'
                default: return 'À faire'
            }
        },
        statusClass(code) {
            switch (code) {
                case 'en_cours':
                    return 'bg-orange-100 text-orange-700'
                case 'terminee':
                    return 'bg-green-100 text-green-700'
                default:
                    return 'bg-gray-100 text-gray-600'
            }
        }
    },
    mounted() {
        this.getInterventions()
    }
}
</script>
